(() => {
  const cardSelector = '.the-moon-home__selection-card';
  const rootSelector = '[data-the-moon-home]';

  const initialiseCard = (card) => {
    if (card.dataset.selectionHoverReady === 'true' || !window.gsap) return;

    const art = card.querySelector(':scope > .the-moon-home__selection-card-art');
    const defaultLabels = Array.from(card.querySelectorAll(':scope > .the-moon-home__selection-default-label'));
    const hover = card.querySelector(':scope > .the-moon-home__selection-card-hover');
    if (!art || !hover) return;

    const panels = Array.from(hover.querySelectorAll('.the-moon-home__selection-card-panel'));
    const content = Array.from(hover.querySelectorAll('.the-moon-home__selection-card-copy, .the-moon-home__selection-card-menu'));
    const media = window.gsap.matchMedia();

    card.dataset.selectionHoverReady = 'true';
    card.selectionHoverMedia = media;

    media.add(
      {
        interactive: '(hover: hover) and (pointer: fine)',
        reduceMotion: '(prefers-reduced-motion: reduce)'
      },
      (context) => {
        if (!context.conditions.interactive) return;

        const animatedElements = [art, hover, ...defaultLabels, ...panels, ...content];
        const coffeeBlue = hover.querySelector('.the-moon-home__selection-card-panel--coffee-blue');
        const coffeeDark = hover.querySelector('.the-moon-home__selection-card-panel--coffee-dark');
        const matchaPanels = hover.querySelector('.the-moon-home__selection-card-panel--matcha');
        const reduceMotion = context.conditions.reduceMotion;

        card.classList.add('is-gsap-hover-ready');
        window.gsap.set(art, { autoAlpha: 1 });
        window.gsap.set(defaultLabels, { autoAlpha: 1 });
        window.gsap.set(hover, { autoAlpha: 0 });
        window.gsap.set(panels, { autoAlpha: 0 });
        window.gsap.set(content, { autoAlpha: 0, y: 14 });
        if (coffeeBlue) window.gsap.set(coffeeBlue, { scale: 0.94, x: -22, y: 18 });
        if (coffeeDark) window.gsap.set(coffeeDark, { scale: 0.92, y: -8 });
        if (matchaPanels) window.gsap.set(matchaPanels, { scale: 0.96, y: 14 });

        const setActive = (active) => {
          card.classList.toggle('is-selection-hover-active', active);
        };

        let timeline = null;
        if (!reduceMotion) {
          timeline = window.gsap.timeline({ paused: true });
          timeline
            .to(art, { autoAlpha: 0, duration: 0.34, ease: 'power2.out', overwrite: 'auto' }, 0)
            .to(defaultLabels, { autoAlpha: 0, duration: 0.2, ease: 'power1.out', overwrite: 'auto' }, 0)
            .to(hover, { autoAlpha: 1, duration: 0.36, ease: 'power2.out', overwrite: 'auto' }, 0)
            .to(
              panels,
              {
                autoAlpha: 1,
                duration: 0.54,
                ease: 'power3.out',
                overwrite: 'auto',
                scale: 1,
                stagger: 0.035,
                x: 0,
                y: 0
              },
              0.04
            )
            .to(
              content,
              {
                autoAlpha: 1,
                duration: 0.42,
                ease: 'power2.out',
                overwrite: 'auto',
                stagger: 0.055,
                y: 0
              },
              0.12
            );
        }

        const enter = () => {
          setActive(true);
          if (timeline) {
            timeline.play();
            return;
          }

          window.gsap.set(art, { autoAlpha: 0 });
          window.gsap.set(defaultLabels, { autoAlpha: 0 });
          window.gsap.set(hover, { autoAlpha: 1 });
          window.gsap.set(panels, { autoAlpha: 1, scale: 1, x: 0, y: 0 });
          window.gsap.set(content, { autoAlpha: 1, y: 0 });
        };

        const leave = () => {
          if (timeline) timeline.pause(0);
          window.gsap.set(art, { autoAlpha: 1 });
          window.gsap.set(defaultLabels, { autoAlpha: 1 });
          window.gsap.set(hover, { autoAlpha: 0 });
          window.gsap.set(panels, { autoAlpha: 0 });
          window.gsap.set(content, { autoAlpha: 0, y: 14 });
          if (coffeeBlue) window.gsap.set(coffeeBlue, { scale: 0.94, x: -22, y: 18 });
          if (coffeeDark) window.gsap.set(coffeeDark, { scale: 0.92, x: 0, y: -8 });
          if (matchaPanels) window.gsap.set(matchaPanels, { scale: 0.96, x: 0, y: 14 });
          setActive(false);
        };

        const handleFocusOut = (event) => {
          if (!card.contains(event.relatedTarget)) leave();
        };

        card.addEventListener('pointerenter', enter);
        card.addEventListener('pointerleave', leave);
        card.addEventListener('focusin', enter);
        card.addEventListener('focusout', handleFocusOut);

        return () => {
          card.removeEventListener('pointerenter', enter);
          card.removeEventListener('pointerleave', leave);
          card.removeEventListener('focusin', enter);
          card.removeEventListener('focusout', handleFocusOut);
          if (timeline) timeline.kill();
          card.classList.remove('is-gsap-hover-ready', 'is-selection-hover-active');
          window.gsap.set(animatedElements, { clearProps: 'opacity,transform,visibility' });
        };
      }
    );
  };

  const initialiseAll = (scope = document, attempt = 0) => {
    if (!window.gsap) {
      if (attempt < 300) window.requestAnimationFrame(() => initialiseAll(scope, attempt + 1));
      return;
    }

    const roots = [];
    if (scope.matches && scope.matches(rootSelector)) roots.push(scope);
    roots.push(...scope.querySelectorAll(rootSelector));
    roots.forEach((root) => root.querySelectorAll(cardSelector).forEach(initialiseCard));
  };

  const destroyAll = (scope) => {
    const cards = [];
    if (scope.matches && scope.matches(cardSelector)) cards.push(scope);
    cards.push(...scope.querySelectorAll(cardSelector));

    cards.forEach((card) => {
      if (card.selectionHoverMedia) card.selectionHoverMedia.revert();
      delete card.selectionHoverMedia;
      delete card.dataset.selectionHoverReady;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
